// eslint-disable-next-line jira/restricted/react-component-props
import React, { type ComponentProps, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import OpenIcon from '@atlaskit/icon/glyph/open';
import { Box, xcss } from '@atlaskit/primitives';
import { components } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import messages from './messages';

export type Props = JSX.LibraryManagedAttributes<
	typeof components.Option,
	ComponentProps<typeof components.Option>
> & {
	data: {
		label: string;
		value: string;
		isLink: boolean;
		link?: string;
	};
	intl: IntlShape;
};

const Option = (props: Props) => {
	const {
		data: { link, label },
	} = props;
	const isLink = link != null;

	const { formatMessage } = useIntl();

	return isLink ? (
		<OuterWrapper>
			<components.Option {...props}>
				<InnerWrapper>
					<OpenIcon size="small" label={formatMessage(messages.openIconLabel)} />
					<Box xcss={labelStyles}>{label}</Box>
				</InnerWrapper>
			</components.Option>
		</OuterWrapper>
	) : (
		<components.Option {...props} />
	);
};

export default Option;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OuterWrapper = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border', colors.N50)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${token('space.100', '8px')} 0 0 ${gridSize * -0.5}px`,
	paddingTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InnerWrapper = styled.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

const labelStyles = xcss({
	paddingLeft: 'space.100',
});
